import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import Fridge from "@/components/Fridge";
import Staff from "@/components/Staff";
import PlayButton from "@/components/PlayButton";
import HebrewKeyboard from "@/components/HebrewKeyboard";
import StudioAuthNav from "@/components/StudioAuthNav";
import LabStamp from "@/components/LabStamp";
import { buildHebrewSequence } from "@/lib/hebrewComposition";
import {
  tokenizeHebrew,
  ROOTS,
  MODES,
  SCALES,
  DEFAULT_ROOT,
  DEFAULT_MODE,
  DEFAULT_SCALE,
  notesForScale,
  alefNoteForMode,
  hebrewLetterToNote,
} from "@/lib/hebrewCryptogram";
import { pickRandomHebrewPhrase } from "@/lib/hebrewWords";
import { unlockAudio, setReverbMix } from "@/lib/synth";
import { measureSequence } from "@/lib/measures";
import { buildMidiFile, downloadMidi } from "@/lib/midiExport";
import { buildMusicXml, noteflightLaunchUrl } from "@/lib/musicXmlExport";
import { playSequenceAudio, playEventsExact } from "@/lib/playEngine";
import {
  PLAYBACK_MODES,
  voiceForPreset,
  defaultBpmFor,
  defaultSustainFor,
  reverbMixForSustain,
  stepMsForBpm,
  BPM_MIN,
  BPM_MAX,
} from "@/lib/presets";
import { logDebug } from "@/lib/log";
import { formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// No word cap — a whole verse or a paragraph of Rambam is fair game. The letter
// cap is only there to stop the staff turning into a smear.
const MAX_LETTERS = 400;
const DEFAULT_PRESET = "meditation";
const PEDAL_ON = true;

export default function HebrewStudio() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputText, setInputText] = useState(() => searchParams.get("words") || "");
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [droppedEntries, setDroppedEntries] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [previewingWordIdx, setPreviewingWordIdx] = useState(-1);
  const [activePreviewKey, setActivePreviewKey] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedEvents, setRecordedEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [root, setRootValue] = useState(() => searchParams.get("root") || DEFAULT_ROOT);
  const [scale, setScaleValue] = useState(() => {
    const s = searchParams.get("scale");
    return SCALES.some((x) => x.value === s) ? s : DEFAULT_SCALE;
  });
  const [playbackMode, setPlaybackMode] = useState(DEFAULT_PRESET);
  const [daily, setDaily] = useState({ loading: true, error: false, sources: [] });
  const [loadedSource, setLoadedSource] = useState(null);
  // Id of the piece we just saved, so its share link is one tap away.
  const [savedId, setSavedId] = useState(null);
  // Bumped by Reset and by each new playback, so a stale loop can't write state.
  const playRunRef = useRef(0);

  const copyShareLink = async () => {
    const id = savedId || (await handleSave());
    if (!id) return;
    const url = `${window.location.origin}/composition/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch {
      toast.message("Share link", { description: url, duration: 8000 });
    }
  };

  const loadDaily = useCallback(async () => {
    setDaily((d) => ({ ...d, loading: true, error: false }));
    try {
      const { data } = await axios.get(`${API}/torah/daily`, {
        params: { weekday: new Date().getDay() },
      });
      setDaily({ loading: false, error: false, sources: data.sources || [] });
    } catch (err) {
      setDaily({ loading: false, error: true, sources: [] });
    }
  }, []);

  useEffect(() => {
    loadDaily();
  }, [loadDaily]);

  const applyDailySource = (src) => {
    setInputText(src.hebrew);
    setLoadedSource(src);
    setSavedId(null);
    clearStaffState();
  };
  // One tempo per feel — Meditative and Groove each remember their own, and
  // both start from the preset's default.
  const [bpmByMode, setBpmByMode] = useState(() => ({
    meditation: defaultBpmFor("meditation"),
    groove: defaultBpmFor("groove"),
  }));
  // Sustain, per feel: how long each note rings (and how much room comes with
  // it). This is the knob that decides whether notes pile up on each other.
  const [sustainByMode, setSustainByMode] = useState(() => ({
    meditation: defaultSustainFor("meditation"),
    groove: defaultSustainFor("groove"),
  }));

  // Studio-only: lay the letters out as eighth notes, a bar (or half bar) per
  // word, padded with rests. Off by default.
  const [measured, setMeasured] = useState(() => searchParams.get("measured") === "1");

  // Studio-only: fold all 22 letters into one octave (1-7 repeating).
  // Off by default, and never offered in the demo app.
  const [condensed, setCondensed] = useState(
    () => searchParams.get("condensed") === "1"
  );
  const [modeIdx, setModeIdxValue] = useState(() => {
    const m = parseInt(searchParams.get("mode") || "", 10);
    return Number.isFinite(m) && m >= 0 && m <= 6 ? m : DEFAULT_MODE;
  });
  const inputRef = useRef(null);
  const stopRef = useRef(false);
  const wordPlayCounterRef = useRef(0);
  const recordingStartRef = useRef(null);
  const recordedEventsRef = useRef([]);
  const recordedTapsRef = useRef([]);
  const recordedDurationMsRef = useRef(0);

  const rawWords = useMemo(
    () => inputText.split(/\s+/).filter((w) => w.length > 0),
    [inputText]
  );
  const words = rawWords;

  const letterCount = useMemo(
    () =>
      words.reduce(
        (acc, w) => acc + tokenizeHebrew(w).length,
        0
      ),
    [words]
  );

  const tooManyLetters = letterCount > MAX_LETTERS;
  const hasBlocker = tooManyLetters;

  const bpm = bpmByMode[playbackMode] ?? defaultBpmFor(playbackMode);
  const setBpm = useCallback(
    (value) => setBpmByMode((prev) => ({ ...prev, [playbackMode]: value })),
    [playbackMode]
  );
  const stepMs = stepMsForBpm(playbackMode, bpm);

  // Playback reads these per note, so dragging a slider mid-play takes effect
  // on the very next note.
  const bpmRef = useRef(bpm);
  const sustainRef = useRef(null);
  bpmRef.current = bpm;
  const sustain = sustainByMode[playbackMode] ?? defaultSustainFor(playbackMode);
  const setSustain = useCallback(
    (value) => setSustainByMode((prev) => ({ ...prev, [playbackMode]: value })),
    [playbackMode]
  );
  const atFeelDefaults =
    bpm === defaultBpmFor(playbackMode) && sustain === defaultSustainFor(playbackMode);
  // Kept in a ref so the export helpers can sit above the memo that builds it.
  const sequenceRef = useRef([]);
  const currentMusicXml = useCallback(() => {
    if (!sequenceRef.current.length) return null;
    const seq = sequenceRef.current;
    return buildMusicXml({
      steps: measured ? measureSequence(seq).steps : seq,
      stepMs,
      measured,
      title: words.join(" ") || "Alephonics",
    });
  }, [measured, stepMs, words]);

  // Noteflight imports the hosted file from its own browser tab, so the piece
  // has to be saved first — that's what gives it a public URL.
  const openInNoteflight = async () => {
    const xml = currentMusicXml();
    if (!xml) {
      toast.error("Type a word first.");
      return;
    }
    const tab = window.open("", "_blank");
    try {
      const id = savedId || (await handleSave());
      if (!id) {
        if (tab) tab.close();
        return;
      }
      await axios.post(`${API}/compositions/${id}/score`, { musicxml: xml }, { withCredentials: true });
      const fileUrl = `${API}/compositions/${id}/score.musicxml`;
      const launch = noteflightLaunchUrl(fileUrl);
      if (tab) tab.location = launch;
      else window.open(launch, "_blank");
      toast.success("Opening in Noteflight — sign in there to save the score.");
    } catch (e) {
      if (tab) tab.close();
      toast.error("Couldn't hand the score to Noteflight.");
    }
  };

  const resetFeel = useCallback(() => {
    setBpm(defaultBpmFor(playbackMode));
    setSustain(defaultSustainFor(playbackMode));
  }, [playbackMode, setBpm, setSustain]);

  sustainRef.current = sustain;

  useEffect(() => {
    setReverbMix(reverbMixForSustain(sustain));
  }, [sustain]);

  const scaleOptions = useMemo(
    () => ({ root, modeIdx, scale, condensed }),
    [root, modeIdx, scale, condensed]
  );

  // Flipping the range re-pitches the notes already on the staff, so they slide
  // from spread out to condensed instead of waiting for the next play.
  const condensedRef = useRef(condensed);
  useEffect(() => {
    if (condensedRef.current === condensed) return;
    condensedRef.current = condensed;
    setDroppedEntries((prev) =>
      prev.length
        ? prev.map((entry) => {
            const note = hebrewLetterToNote(entry.letter, { root, modeIdx, scale, condensed });
            return note ? { ...entry, ...note } : entry;
          })
        : prev
    );
  }, [condensed, root, modeIdx, scale]);
  const sequence = useMemo(
    () => buildHebrewSequence(words, scaleOptions),
    [words, scaleOptions]
  );
  sequenceRef.current = sequence;


  // What playback and the MIDI export both read: measured adds the rests.
  const measuredSteps = useMemo(
    () => (measured ? measureSequence(sequence).steps : null),
    [measured, sequence]
  );
  const playSteps = measuredSteps || sequence;

  const exportMidi = useCallback(() => {
    if (!sequence.length) {
      toast.error("Type a word first.");
      return;
    }
    const steps = measured ? measureSequence(sequence).steps : sequence;
    const blob = buildMidiFile({
      steps,
      stepMs,
      measured,
      root,
      title: words.join(" ") || "Alephonics",
    });
    const name = (words.join("-") || "alephonics").replace(/[^\w\-\u0590-\u05FF]+/g, "").slice(0, 40);
    downloadMidi(blob, `${name || "alephonics"}${measured ? "-measured" : ""}.mid`);
    toast.success(measured ? "MIDI exported with bars and rests." : "MIDI exported.");
  }, [sequence, measured, stepMs, root, words]);

  // The scale itself never changes with mode — it's always [key] [scale].
  // Mode determines which scale degree alef sits on.
  const scaleLabel = useMemo(() => notesForScale(root, scale).join(" "), [root, scale]);

  // Mode dropdown labels: which note alef starts on for each mode.
  const modeOptions = useMemo(
    () =>
      MODES.map((m) => ({
        value: m.value,
        label: `Aleph on ${alefNoteForMode(root, m.value, scale)}`,
      })),
    [root, scale]
  );

  const droppedSet = useMemo(() => {
    const s = new Set();
    droppedEntries.forEach((e) => s.add(`${e.wordIdx}:${e.charIdx}`));
    return s;
  }, [droppedEntries]);

  const hasPlayed =
    !isPlaying && sequence.length > 0 && droppedEntries.length === sequence.length;

  const clearStaffState = () => {
    setDroppedEntries([]);
    setActiveIndex(-1);
    setRecordedEvents([]);
    recordedEventsRef.current = [];
    recordedTapsRef.current = [];
    recordedDurationMsRef.current = 0;
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    // Typing over a pulled-in line means it's no longer that day's learning.
    setLoadedSource(null);
    setSavedId(null);
    if (searchParams.get("words")) {
      const np = new URLSearchParams(searchParams);
      np.delete("words");
      setSearchParams(np, { replace: true });
    }
    if (!isPlaying) clearStaffState();
  };

  const setRoot = (v) => {
    setRootValue(v);
    if (!isPlaying) clearStaffState();
  };

  const setScale = (v) => {
    setScaleValue(v);
    if (!isPlaying) clearStaffState();
  };

  const setModeIdx = (v) => {
    setModeIdxValue(v);
    if (!isPlaying) clearStaffState();
  };

  const handleInspireMe = () => {
    setInputText(pickRandomHebrewPhrase(5, 24));
    clearStaffState();
  };

  // Insert at the input's current caret position (so on-screen keyboard taps
  // edit where the user is, not always at the end).
  const insertAtCaret = (text) => {
    const el = inputRef.current;
    if (!el || typeof el.selectionStart !== "number") {
      setInputText((prev) => prev + text);
      if (!isPlaying) clearStaffState();
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = el.value.slice(0, start) + text + el.value.slice(end);
    setInputText(next);
    if (!isPlaying) clearStaffState();
    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (!node) return;
      const pos = start + text.length;
      try {
        node.focus();
        node.setSelectionRange(pos, pos);
      } catch (err) {
        // input may not support setSelectionRange (some mobile inputs)
        logDebug("setSelectionRange unsupported", err);
      }
    });
  };

  const appendChar = (ch) => insertAtCaret(ch);
  const handleKeyboardSpace = () => insertAtCaret(" ");
  const handleKeyboardBackspace = () => {
    const el = inputRef.current;
    if (!el || typeof el.selectionStart !== "number") {
      setInputText((prev) => [...prev].slice(0, -1).join(""));
      if (!isPlaying) clearStaffState();
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    let nextValue;
    let nextPos;
    if (start !== end) {
      // delete the selected range
      nextValue = el.value.slice(0, start) + el.value.slice(end);
      nextPos = start;
    } else if (start > 0) {
      nextValue = el.value.slice(0, start - 1) + el.value.slice(start);
      nextPos = start - 1;
    } else {
      return;
    }
    setInputText(nextValue);
    if (!isPlaying) clearStaffState();
    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (!node) return;
      try {
        node.focus();
        node.setSelectionRange(nextPos, nextPos);
      } catch (err) {
        logDebug("setSelectionRange unsupported", err);
      }
    });
  };

  const reset = useCallback(() => {
    stopRef.current = true;
    // Invalidate any run in flight so a late finish can't wipe the fresh state.
    playRunRef.current += 1;
    wordPlayCounterRef.current += 1;
    setIsPlaying(false);
    setIsRecording(false);
    clearStaffState();
    setPreviewingWordIdx(-1);
    setActivePreviewKey(null);
  }, []);

  const handlePlay = async () => {
    if (isPlaying || hasBlocker || sequence.length === 0) return;
    unlockAudio();
    stopRef.current = false;
    const runId = playRunRef.current + 1;
    playRunRef.current = runId;
    const isStale = () => playRunRef.current !== runId;
    setIsPlaying(true);
    setDroppedEntries([]);
    setActiveIndex(-1);
    const collected = [];
    await playSequenceAudio(playSteps, {
      presetKey: playbackMode,
      measured,
      bpm: () => bpmRef.current,
      sustain: () => sustainRef.current,
      pedal: false,
      shouldStop: () => stopRef.current || isStale(),
      onTick: (i, entry, stepMs) => {
        if (isStale() || entry.rest) return;
        collected.push({ ...entry, stepMs });
        setDroppedEntries([...collected]);
        setActiveIndex(collected.length - 1);
      },
    });
    // Reset (or a newer run) already took over — leave its state alone.
    if (isStale()) return;
    setActiveIndex(-1);
    setIsPlaying(false);
  };

  const playWord = async (wordIdx) => {
    if (isPlaying) return;
    const wordSeq = sequence.filter((e) => e.wordIdx === wordIdx);
    if (wordSeq.length === 0) return;
    unlockAudio();
    const myId = wordPlayCounterRef.current + 1;
    wordPlayCounterRef.current = myId;
    setPreviewingWordIdx(wordIdx);
    setActivePreviewKey(null);
    const recording = isRecording;
    let virtualSeq = wordSeq;
    if (recording) {
      const now = Date.now();
      const elapsedMs = now - (recordingStartRef.current || now);
      const gapMs = elapsedMs - recordedDurationMsRef.current;
      if (gapMs > 30 && recordedEventsRef.current.length > 0) {
        recordedEventsRef.current.push({ kind: "rest", durMs: gapMs });
        recordedDurationMsRef.current += gapMs;
      }
      recordedTapsRef.current.push({ wordIdx, atMs: elapsedMs });
      const tapOrd = recordedTapsRef.current.length;
      virtualSeq = wordSeq.map((e) => ({
        ...e,
        wordIdx: `tap-${tapOrd}`,
        displayWordIdx: e.wordIdx,
      }));
    }
    await playSequenceAudio(virtualSeq, {
      presetKey: playbackMode,
      bpm: () => bpmRef.current,
      sustain: () => sustainRef.current,
      pedal: false,
      shouldStop: () => wordPlayCounterRef.current !== myId,
      onTick: (_i, entry, stepMs, velocity, noteSec) => {
        const origIdx = entry.displayWordIdx ?? entry.wordIdx;
        setActivePreviewKey(`${origIdx}:${entry.charIdx}`);
        if (recording) {
          recordedEventsRef.current.push({
            kind: "note",
            pitch: entry.pitch,
            note: entry.note,
            accidental: entry.accidental || null,
            octave: entry.octave,
            letter: entry.letter,
            charIdx: entry.charIdx,
            displayWordIdx: origIdx,
            stepMs,
            velocity,
            noteSec,
          });
          recordedDurationMsRef.current += stepMs;
          setDroppedEntries((prev) => [
            ...prev,
            { ...entry, stepMs, displayWordIdx: origIdx },
          ]);
        }
      },
    });
    if (wordPlayCounterRef.current === myId) {
      setPreviewingWordIdx(-1);
      setActivePreviewKey(null);
    }
  };

  const startRecording = () => {
    if (isPlaying) return;
    stopRef.current = true;
    setIsRecording(true);
    setRecordedEvents([]);
    recordingStartRef.current = Date.now();
    recordedEventsRef.current = [];
    recordedTapsRef.current = [];
    recordedDurationMsRef.current = 0;
    setDroppedEntries([]);
    setActiveIndex(-1);
    setPreviewingWordIdx(-1);
    setActivePreviewKey(null);
    toast.info("Recording — tap words in any order.");
  };

  const stopRecording = () => {
    setIsRecording(false);
    const events = recordedEventsRef.current.slice();
    const noteCount = events.filter((e) => e.kind === "note").length;
    if (noteCount === 0) {
      toast.info("Tap some words first — nothing was recorded.");
      return;
    }
    setRecordedEvents(events);
    toast.success(`Captured ${noteCount} notes — hit Save to keep it.`);
  };

  const handleSave = async () => {
    if (hasBlocker) {
      toast.error(`Keep it to ${MAX_LETTERS} letters or fewer.`);
      return null;
    }
    const cleanedWords = words.map((w) => w.trim()).filter(Boolean);
    if (cleanedWords.length === 0) {
      toast.error("Type at least one word before saving.");
      return null;
    }
    const hasRemix = recordedEvents.length > 0;
    const baseTitle = cleanedWords.join(" ").slice(0, 60);
    const finalTitle = hasRemix ? `${baseTitle.slice(0, 40)} (remix)` : baseTitle;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/compositions`, {
        title: finalTitle,
        words: cleanedWords,
        preset_key: playbackMode,
        ...(loadedSource
          ? {
              source_label: `${loadedSource.label}${loadedSource.display ? ` · ${loadedSource.display}` : ""}`,
              source_ref: loadedSource.ref,
            }
          : {}),
        pedal: PEDAL_ON,
        scale_root: root,
        scale_mode: modeIdx,
        scale_family: scale,
        measured,
        condensed,
        ...(currentMusicXml() ? { score_xml: currentMusicXml() } : {}),
        ...(hasRemix
          ? {
              remix_taps: recordedTapsRef.current.slice(),
              remix_events: recordedEvents,
            }
          : {}),
      }, { withCredentials: true });
      setSavedId(data?.id || null);
      toast.success(`Saved "${finalTitle}" to your library`);
      return data?.id || null;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          ? `Couldn't save: ${formatApiErrorDetail(e)}`
          : "Couldn't save composition"
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const playRemix = async () => {
    if (isPlaying || isRecording || recordedEvents.length === 0) return;
    unlockAudio();
    stopRef.current = false;
    setIsPlaying(true);
    setDroppedEntries([]);
    setActiveIndex(-1);
    let noteCounter = 0;
    await playEventsExact(recordedEvents, {
      voice: voiceForPreset(playbackMode),
      shouldStop: () => stopRef.current,
      onNote: (ev) => {
        const entry = {
          note: ev.note,
          accidental: ev.accidental,
          octave: ev.octave,
          pitch: ev.pitch,
          letter: ev.letter,
          charIdx: ev.charIdx,
          wordIdx: `remix-${noteCounter}`,
          displayWordIdx: ev.displayWordIdx,
          stepMs: ev.stepMs,
        };
        const idx = noteCounter;
        noteCounter += 1;
        setDroppedEntries((prev) => [...prev, entry]);
        setActiveIndex(idx);
      },
    });
    setActiveIndex(-1);
    setIsPlaying(false);
  };

  return (
    <div
      className="min-h-screen w-full"
      style={{ backgroundColor: "#FAF8F5", color: "#1A1523", fontFamily: "'Courier Prime', monospace" }}
    >
      <Toaster position="bottom-right" richColors />

      <header className="max-w-[1100px] mx-auto px-5 sm:px-6 pt-8 sm:pt-10 pb-6 flex flex-wrap items-end justify-between gap-4 sm:gap-6">
        <div className="min-w-0 flex-1">
          <LabStamp testId="studio-lab-stamp" />
          <h1
            className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            data-testid="hebrew-studio-title"
          >
            The Alephonics App<span style={{ color: "#E86F4A" }}>.</span>
          </h1>
          <p className="mt-3 text-sm max-w-[520px] leading-relaxed" style={{ color: "#5C5268" }}>
            Every Hebrew letter mapped to a note across three octaves. Pick the scale,
            the key and where aleph starts, then hear the word.
          </p>
        </div>
        <nav className="text-sm shrink-0 flex gap-4 items-center">
          <StudioAuthNav />
          <Link to="/app/harp" className="underline underline-offset-4 hover:text-[#E86F4A]" data-testid="nav-harp-link">
            Harp &rsaquo;
          </Link>
          <Link to="/app/library" className="underline underline-offset-4 hover:text-[#E86F4A]" data-testid="nav-library-link">
            Library &rsaquo;
          </Link>
        </nav>
      </header>

      <main className="max-w-[1100px] mx-auto px-5 sm:px-6 pb-20 space-y-6 sm:space-y-8">
        <section
          className="rounded-md p-4 sm:p-6 border"
          style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <h2 className="text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Type or paste Hebrew
            </h2>
            <div className="flex gap-4 text-xs" style={{ color: "#5C5268" }}>
              <span>{rawWords.length} {rawWords.length === 1 ? "word" : "words"}</span>
              <span style={{ color: tooManyLetters ? "#D99B26" : "#5C5268" }}>
                {letterCount} / {MAX_LETTERS} letters
              </span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="hebrew-input"
              ref={inputRef}
              value={inputText}
              onChange={handleInputChange}
              dir="rtl"
              placeholder="שלום אהבה אור"
              className="flex-1 bg-[#FFFFFF] border-[#EBE3D5] text-[#1A1523] placeholder:text-[#5C5268] h-12 text-xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              autoFocus
            />
            <Button
              data-testid="hebrew-inspire-button"
              onClick={handleInspireMe}
              disabled={isPlaying || isRecording}
              variant="outline"
              className="h-12 rounded-md border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5 whitespace-nowrap w-full sm:w-auto"
              style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.14em" }}
              title="Pick a few Tanach words for me"
            >
              ✦ Inspire me
            </Button>
            <Button
              data-testid="hebrew-keyboard-toggle"
              onClick={() => setShowKeyboard((v) => !v)}
              variant="outline"
              className="h-12 rounded-md border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5 whitespace-nowrap w-full sm:w-auto"
              style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.14em" }}
            >
              {showKeyboard ? "Hide keyboard" : "Keyboard"}
            </Button>
          </div>
          {hasBlocker ? (
            <p className="mt-2 text-xs" style={{ color: "#8A6514" }}>
              That&apos;s a lot of letters — keep the total to {MAX_LETTERS} or fewer.
            </p>
          ) : (
            <p className="mt-2 text-xs" style={{ color: "#5C5268" }}>
              Separate words with spaces. Tap any word to hear it on its own.
            </p>
          )}

          {/* Today's learning, straight from Sefaria — one line from each cycle. */}
          <div className="mt-5 pt-4" style={{ borderTop: "1px solid #EBE3D5" }}>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="text-[10px] uppercase mr-1"
                style={{ color: "#5C5268", letterSpacing: "0.18em", fontFamily: "'Courier Prime', monospace" }}
              >
                Today&apos;s learning
              </span>
              {daily.loading && (
                <span className="text-xs" style={{ color: "#5C5268" }} data-testid="daily-loading">
                  asking Sefaria…
                </span>
              )}
              {daily.error && (
                <button
                  type="button"
                  onClick={loadDaily}
                  className="text-xs underline underline-offset-4"
                  style={{ color: "#8A6514" }}
                  data-testid="daily-retry"
                >
                  Couldn&apos;t reach Sefaria — try again
                </button>
              )}
              {daily.sources.map((src) => (
                <button
                  key={src.key}
                  type="button"
                  onClick={() => applyDailySource(src)}
                  disabled={isPlaying || isRecording}
                  data-testid={`daily-source-${src.key}`}
                  title={src.ref}
                  className="h-9 px-3 rounded-md text-xs transition-colors disabled:opacity-40"
                  style={{
                    fontFamily: "'Courier Prime', monospace",
                    letterSpacing: "0.1em",
                    border: "1px solid #EBE3D5",
                    backgroundColor: loadedSource?.key === src.key ? "rgba(217,155,38,0.16)" : "#FFFFFF",
                    color: loadedSource?.key === src.key ? "#8A6514" : "#1A1523",
                  }}
                >
                  {src.label}
                </button>
              ))}
            </div>
            {loadedSource && (
              <div className="mt-3" data-testid="daily-loaded">
                <p className="text-xs" style={{ color: "#E86F4A", fontFamily: "'Courier Prime', monospace" }}>
                  {loadedSource.label}
                  {loadedSource.display ? ` · ${loadedSource.display}` : ""} · {loadedSource.ref}
                </p>
                {loadedSource.english && (
                  <p className="mt-1.5 text-sm max-w-[80ch]" style={{ color: "#5C5268", lineHeight: 1.65 }}>
                    {loadedSource.english}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {showKeyboard && (
          <HebrewKeyboard theme="light"
            onLetter={appendChar}
            onSpace={handleKeyboardSpace}
            onBackspace={handleKeyboardBackspace}
            disabled={isPlaying}
          />
        )}

        <section
          className="rounded-md p-4 sm:p-5 border flex flex-col gap-5"
          style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}
          data-testid="hebrew-scale-controls"
        >
          <div
            className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-5"
            data-testid="hebrew-controls-notes"
          >
          <div className="flex-1 min-w-0">
            <label
              className="block text-[10px] uppercase tracking-[0.18em] mb-1.5"
              style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
            >
              Scale
            </label>
            <Select value={scale} onValueChange={(v) => setScale(v)} disabled={isRecording}>
              <SelectTrigger
                data-testid="hebrew-scale-select"
                className="h-11 bg-[#FFFFFF] border-[#EBE3D5] text-[#1A1523] placeholder:text-[#5C5268] text-base"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                <SelectValue placeholder="Pick a scale" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFBF2] border-[#EBE3D5] text-[#1A1523]">
                {SCALES.map((s) => (
                  <SelectItem
                    key={s.value}
                    value={s.value}
                    data-testid={`hebrew-scale-option-${s.value}`}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-0">
            <label
              className="block text-[10px] uppercase tracking-[0.18em] mb-1.5"
              style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
            >
              Key
            </label>
            <Select value={root} onValueChange={(v) => setRoot(v)} disabled={isRecording}>
              <SelectTrigger
                data-testid="hebrew-root-select"
                className="h-11 bg-[#FFFFFF] border-[#EBE3D5] text-[#1A1523] placeholder:text-[#5C5268] text-base"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                <SelectValue placeholder="Pick a key" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFBF2] border-[#EBE3D5] text-[#1A1523]">
                {ROOTS.map((r) => (
                  <SelectItem
                    key={r.value}
                    value={r.value}
                    data-testid={`hebrew-root-option-${r.value}`}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-[1.4] min-w-0">
            <label
              className="block text-[10px] uppercase tracking-[0.18em] mb-1.5"
              style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
            >
              Mode (starting note)
            </label>            <Select
              value={String(modeIdx)}
              onValueChange={(v) => setModeIdx(parseInt(v, 10))}
              disabled={isRecording}
            >
              <SelectTrigger
                data-testid="hebrew-mode-select"
                className="h-11 bg-[#FFFFFF] border-[#EBE3D5] text-[#1A1523] placeholder:text-[#5C5268] text-sm"
                style={{ fontFamily: "'Courier Prime', monospace" }}
              >
                <SelectValue placeholder="Pick a mode" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFBF2] border-[#EBE3D5] text-[#1A1523]">
                {modeOptions.map((m) => (
                  <SelectItem
                    key={m.value}
                    value={String(m.value)}
                    data-testid={`hebrew-mode-option-${m.value}`}
                    style={{ fontFamily: "'Courier Prime', monospace" }}
                  >
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          </div>

          <div
            className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-5 pt-4"
            style={{ borderTop: "1px solid #EBE3D5" }}
            data-testid="hebrew-controls-sound"
          >
          <div className="min-w-0">
            <label
              className="block text-[10px] uppercase tracking-[0.18em] mb-1.5"
              style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
            >
              Range
            </label>
            <button
              type="button"
              role="switch"
              aria-checked={condensed}
              onClick={() => setCondensed((v) => !v)}
              disabled={isRecording}
              data-testid="hebrew-condensed-toggle"
              title="Fold all 22 letters into one octave (1-7 repeating)"
              className="h-11 px-4 flex items-center gap-3 text-xs uppercase tracking-[0.16em] transition-colors disabled:opacity-40"
              style={{
                fontFamily: "'Courier Prime', monospace",
                border: "1px solid #EBE3D5",
                backgroundColor: condensed ? "#1B6C7A" : "#FFFFFF",
                color: condensed ? "#1A1523" : "#5C5268",
              }}
            >
              <span
                aria-hidden="true"
                className="relative inline-block shrink-0 transition-colors"
                style={{
                  width: 30,
                  height: 16,
                  borderRadius: 999,
                  backgroundColor: condensed ? "#D99B26" : "#EBE3D5",
                }}
              >
                <span
                  className="absolute top-[2px] transition-all"
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    left: condensed ? 16 : 2,
                    backgroundColor: condensed ? "#FFFBF2" : "#5C5268",
                  }}
                />
              </span>
              Condensed
            </button>
          </div>

          <div className="min-w-0">
            <label
              className="block text-[10px] uppercase tracking-[0.18em] mb-1.5"
              style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
            >
              Bars
            </label>
            <button
              type="button"
              role="switch"
              aria-checked={measured}
              onClick={() => setMeasured((v) => !v)}
              disabled={isRecording}
              data-testid="hebrew-measured-toggle"
              title="Eighth notes, a bar (or half bar) per word, padded with rests"
              className="h-11 px-4 flex items-center gap-3 text-xs uppercase tracking-[0.16em] transition-colors disabled:opacity-40"
              style={{
                fontFamily: "'Courier Prime', monospace",
                border: "1px solid #EBE3D5",
                backgroundColor: measured ? "#1B6C7A" : "#FFFFFF",
                color: measured ? "#1A1523" : "#5C5268",
              }}
            >
              <span
                aria-hidden="true"
                className="relative inline-block shrink-0 transition-colors"
                style={{ width: 30, height: 16, borderRadius: 999, backgroundColor: measured ? "#D99B26" : "#EBE3D5" }}
              >
                <span
                  className="absolute top-[2px] transition-all"
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    left: measured ? 16 : 2,
                    backgroundColor: measured ? "#FFFBF2" : "#5C5268",
                  }}
                />
              </span>
              Measured
            </button>
          </div>

          <div className="min-w-0">
            <label
              className="block text-[10px] uppercase tracking-[0.18em] mb-1.5"
              style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
            >
              Feel
            </label>
            <div className="flex" data-testid="hebrew-playback-mode">
              {PLAYBACK_MODES.map((m, i) => {
                const active = playbackMode === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setPlaybackMode(m.value)}
                    disabled={isRecording}
                    data-testid={`hebrew-playback-${m.value}`}
                    className="h-11 px-4 text-xs uppercase tracking-[0.16em] transition-colors disabled:opacity-40"
                    style={{
                      fontFamily: "'Courier Prime', monospace",
                      border: "1px solid #EBE3D5",
                      borderLeftWidth: i === 0 ? 1 : 0,
                      backgroundColor: active ? "#1B6C7A" : "#FFFFFF",
                      color: active ? "#1A1523" : "#5C5268",
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 min-w-[170px]">
            <label
              className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.18em] mb-1.5"
              style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
              htmlFor="hebrew-bpm"
            >
              <span>Tempo</span>
              <span style={{ color: "#8A6514" }} data-testid="hebrew-bpm-value">
                {bpm} bpm
              </span>
            </label>
            <div className="h-11 flex items-center gap-3">
              <input
                id="hebrew-bpm"
                type="range"
                min={BPM_MIN}
                max={BPM_MAX}
                step={1}
                value={bpm}
                onChange={(e) => setBpm(parseInt(e.target.value, 10))}
                disabled={isRecording}
                data-testid="hebrew-bpm-slider"
                className="studio-range flex-1 min-w-0"
              />
              <button
                type="button"
                onClick={resetFeel}
                disabled={isRecording || atFeelDefaults}
                data-testid="hebrew-feel-reset"
                className="text-[10px] uppercase tracking-[0.16em] transition-colors disabled:opacity-30 hover:text-[#8A6514]"
                style={{ color: "#E86F4A", fontFamily: "'Courier Prime', monospace" }}
                title={`Back to the ${playbackMode === "groove" ? "Groove" : "Meditative"} tempo and sustain`}
              >
                Reset
              </button>
            </div>
          </div>

          <div className="flex-1 min-w-[150px]">
            <label
              className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.18em] mb-1.5"
              style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
              htmlFor="hebrew-sustain"
            >
              <span>Sustain</span>
              <span style={{ color: "#8A6514" }} data-testid="hebrew-sustain-value">
                {sustain}%
              </span>
            </label>
            <div className="h-11 flex items-center">
              <input
                id="hebrew-sustain"
                type="range"
                min={0}
                max={200}
                step={5}
                value={sustain}
                onChange={(e) => setSustain(parseInt(e.target.value, 10))}
                disabled={isRecording}
                title="How long each note rings, and how much room comes with it"
                data-testid="hebrew-sustain-slider"
                className="studio-range w-full"
              />
            </div>
          </div>
          </div>
        </section>

        <section>
          <Fridge
            words={words}
            droppedSet={droppedSet}
            onWordClick={playWord}
            previewingWordIdx={previewingWordIdx}
            interactive={!isPlaying}
            hasPlayed={hasPlayed}
          />
          {hasPlayed && (
            <p
              className="mt-3 text-center text-sm"
              style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
            >
              <span style={{ color: "#E86F4A" }}>↻</span>&nbsp; Tap any word to hear it on its own.
            </p>
          )}
        </section>

        <section className="flex flex-wrap items-center justify-center gap-3">
          <PlayButton
            testId="hebrew-play-button"
            onClick={recordedEvents.length > 0 ? playRemix : handlePlay}
            disabled={isPlaying || sequence.length === 0 || hasBlocker || isRecording}
            label={isPlaying ? "Playing…" : recordedEvents.length > 0 ? "Play Remix" : "Play"}
          />
          <Button
            data-testid="hebrew-reset-button"
            onClick={reset}
            variant="outline"
            className="rounded-full border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
          >
            Reset
          </Button>
          <Button
            data-testid="hebrew-save-button"
            onClick={handleSave}
            disabled={saving || sequence.length === 0 || hasBlocker || isRecording}
            variant="outline"
            className="rounded-full border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
          >
            {saving ? "Saving…" : recordedEvents.length > 0 ? "Save Remix" : "Save"}
          </Button>
          <Button
            data-testid="hebrew-copy-link-button"
            onClick={copyShareLink}
            disabled={saving || sequence.length === 0 || hasBlocker}
            variant="outline"
            className="rounded-full border-[#D99B26] text-[#8A6514] hover:bg-[#D99B26]/10"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
            title={savedId ? "Copy this score's link" : "Saves to your library, then copies the link"}
          >
            {savedId ? "Copy link" : "Share"}
          </Button>
          <Button
            data-testid="hebrew-export-midi-button"
            onClick={exportMidi}
            disabled={sequence.length === 0 || isRecording}
            variant="outline"
            className="rounded-full border-[#D6CBB8] text-[#1A1523] hover:bg-[#F3EFE8]"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
            title="Download a .mid file — for DAWs and other scoring software"
          >
            Export MIDI
          </Button>
          <Button
            data-testid="hebrew-noteflight-button"
            onClick={openInNoteflight}
            disabled={saving || sequence.length === 0 || hasBlocker || isRecording}
            variant="outline"
            className="rounded-full border-[#D6CBB8] text-[#1A1523] hover:bg-[#F3EFE8]"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
            title="Saves the score, then opens it in Noteflight with these exact bars and rests"
          >
            Open in Noteflight
          </Button>

          <Button
            data-testid="hebrew-record-button"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={(isPlaying && !isRecording) || sequence.length === 0 || hasBlocker}
            className="rounded-full text-white"
            style={{
              backgroundColor: isRecording ? "#B8862B" : "#1B6C7A",
              fontFamily: "'Courier Prime', monospace",
              letterSpacing: "0.18em",
            }}
          >
            {isRecording ? "■ Stop Recording" : "● Record Remix"}
          </Button>
        </section>

        {isRecording && (
          <p
            className="text-center text-sm"
            style={{ color: "#E86F4A", fontFamily: "'Courier Prime', monospace" }}
          >
            <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>
              ●
            </motion.span>
            &nbsp; Recording — tap words in any order.
          </p>
        )}

        <section>
          <Staff theme="light"
            droppedEntries={droppedEntries}
            activeIndex={activeIndex}
            previewingWordIdx={previewingWordIdx}
            previewingKey={activePreviewKey}
            baseStepMs={stepMs}
            measuredSteps={measured ? measuredSteps : null}
            noteStyle={playbackMode === "groove" ? "groove" : "meditative"}
          />
          <div className="mt-3 flex items-center justify-between text-xs" style={{ color: "#5C5268" }}>
            <span data-testid="hebrew-scale-label">
              {ROOTS.find((r) => r.value === root)?.label || root}{" "}
              {SCALES.find((s) => s.value === scale)?.label || ""}
              &nbsp;&mdash;&nbsp; {scaleLabel}
              &nbsp;&middot;&nbsp; aleph on {alefNoteForMode(root, modeIdx, scale)}
              &nbsp;&middot;&nbsp;
              <span data-testid="hebrew-range-label">
                {condensed ? "condensed (1-7 repeating)" : "full range (1-22)"}
              </span>
            </span>
            <span>
              {droppedEntries.length} / {sequence.length} letters
            </span>
          </div>
        </section>
      </main>
    </div>
  );
}
